import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../utils/api';
import { formatDate, getStatusBadgeClass, formatStatusText } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminTableLoadingOverlay from '../../components/admin/AdminTableLoadingOverlay';
import { useDebounce } from '../../hooks/useDebounce';

const FETCH_LIMIT = 500;

const AdminTickets = () => {
  const [rawTickets, setRawTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    fetchTickets();
  }, [debouncedSearch]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getTickets({
        search: debouncedSearch || undefined,
        skip: 0,
        limit: FETCH_LIMIT,
      });
      setRawTickets(response.data.tickets);
      setPage(1);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const filteredTickets = statusFilter
    ? rawTickets.filter((t) => t.status === statusFilter)
    : rawTickets;
  const total = filteredTickets.length;
  const tickets = filteredTickets.slice((page - 1) * pageSize, page * pageSize);

  const handleReply = async () => {
    try {
      await adminAPI.replyTicket(selectedTicket.id, replyMessage);
      toast.success('Reply sent');
      setReplyMessage('');
      setSelectedTicket(null);
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const handleClose = async (ticketId) => {
    try {
      await adminAPI.closeTicket(ticketId);
      toast.success('Ticket closed');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };

  const filterOptions = [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ];

  if (loading && rawTickets.length === 0) return <div className="flex items-center justify-center min-h-[60vh]"><div className="loading-spinner"></div></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-tickets">
      <h1 className="text-2xl sm:text-4xl font-bold text-foreground">Support Tickets</h1>

      <AdminTableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by user, subject..."
        filterValue={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); setPage(1); }}
        filterOptions={filterOptions}
        filterPlaceholder="All statuses"
        total={total}
        loading={loading}
      />

      <Card className="glass-card p-6 overflow-hidden">
        <AdminTableLoadingOverlay loading={loading}>
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">User</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Subject</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Status</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Created</th>
                <th className="text-left text-muted-foreground text-sm font-medium pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr key={ticket.id} className="table-row" data-testid={`ticket-row-${ticket.id}`}>
                  <td className="py-4">
                    <div>
                      <p className="text-foreground">{ticket.user?.full_name || '—'}</p>
                      <p className="text-muted-foreground text-sm">{ticket.user?.email || '—'}</p>
                    </div>
                  </td>
                  <td className="py-4 text-foreground">{ticket.subject}</td>
                  <td className="py-4">
                    <span className={`badge ${getStatusBadgeClass(ticket.status)}`}>
                      {formatStatusText(ticket.status)}
                    </span>
                  </td>
                  <td className="py-4 text-muted-foreground text-sm">{formatDate(ticket.created_at)}</td>
                  <td className="py-4">
                    {ticket.status !== 'closed' && (
                      <div className="flex space-x-2">
                        <Button size="sm" onClick={() => setSelectedTicket(ticket)} data-testid={`reply-ticket-btn-${ticket.id}`}>
                          Reply
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleClose(ticket.id)} data-testid={`close-ticket-btn-${ticket.id}`}>
                          Close
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPagination page={page} pageSize={pageSize} onPageSizeChange={(v) => { setPageSize(v); setPage(1); }} total={total} onPageChange={setPage} loading={loading} />
        </AdminTableLoadingOverlay>
      </Card>

      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Reply to Ticket</DialogTitle>
          </DialogHeader>
          {selectedTicket && (
            <div className="space-y-4">
              <div>
                <p className="text-muted-foreground text-sm mb-2">Subject: {selectedTicket.subject}</p>
              </div>
              {/* Show user messages */}
              <div className="mb-4">
                <p className="text-foreground font-semibold mb-2">User Messages:</p>
                <div className="space-y-2">
                  {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                    selectedTicket.messages.map((msg, idx) => (
                      <div key={idx} className="bg-muted rounded p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-foreground">{msg.sender_name}</span>
                          <span className="text-xs text-muted-foreground">{new Date(msg.timestamp).toLocaleString()}</span>
                        </div>
                        <div className="text-foreground/90">{msg.message}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-muted-foreground">No messages yet.</div>
                  )}
                </div>
              </div>
              <Textarea value={replyMessage} onChange={(e) => setReplyMessage(e.target.value)} className="input-field min-h-[120px]" placeholder="Type your reply..." data-testid="reply-message-textarea" />
              <Button onClick={handleReply} className="w-full btn-primary" data-testid="send-reply-button">
                Send Reply
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTickets;
